import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Upload, FileSpreadsheet, Sparkles, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const IGNORE = "__ignore__";

export default function DatenImport() {
  const [step, setStep] = useState(1); // 1=upload, 2=preview+map, 3=result
  const [sheets, setSheets] = useState([]);
  const [fieldOptions, setFieldOptions] = useState([]);
  const [activeSheet, setActiveSheet] = useState(0);
  const [mapping, setMapping] = useState({}); // {header: field_key}
  const [createProject, setCreateProject] = useState(false);
  const [defaultProjectType, setDefaultProjectType] = useState("pv");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.download = "import_vorlage.xlsx";
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Vorlage konnte nicht geladen werden");
    }
  };

  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSheets(data.sheets || []);
      setFieldOptions(data.field_options || []);
      setActiveSheet(0);
      const first = (data.sheets || [])[0];
      setMapping(first?.auto_mapping || {});
      setStep(2);
      toast.success(`${data.sheets?.length || 0} Sheet(s) gelesen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const changeSheet = (idx) => {
    setActiveSheet(idx);
    setMapping(sheets[idx]?.auto_mapping || {});
  };

  const execute = async () => {
    const sheet = sheets[activeSheet];
    if (!sheet) return;
    setBusy(true);
    try {
      const cleanMapping = Object.fromEntries(
        Object.entries(mapping).filter(([_, v]) => v && v !== IGNORE)
      );
      const { data } = await api.post("/import/execute", {
        mapping: cleanMapping,
        rows: sheet.rows,
        create_project: createProject,
        default_project_type: defaultProjectType,
      });
      setResult(data);
      setStep(3);
      toast.success(`${data.created_anmeldungen} Anmeldung(en) importiert`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const currentSheet = sheets[activeSheet];
  const mappedCount = Object.values(mapping).filter((v) => v && v !== IGNORE).length;

  return (
    <div className="p-4 lg:p-8 space-y-6 animate-fade-up max-w-6xl" data-testid="import-page">
      <div>
        <Link to="/einstellungen" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="h-3 w-3" /> Zurück zu Einstellungen
        </Link>
        <div className="overline">Daten-Migration</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Excel-Import</h1>
        <p className="text-muted-foreground mt-1">Bestehende Kundenlisten aus Excel oder CSV in einem Rutsch übernehmen.</p>
      </div>

      {/* Step-Indicator */}
      <div className="flex items-center gap-2 text-xs">
        {["Datei", "Zuordnung", "Import"].map((label, i) => {
          const active = step === i + 1;
          const done = step > i + 1;
          return (
            <div key={label} className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${active ? "bg-primary text-primary-foreground" : done ? "bg-emerald-100 text-emerald-900" : "bg-muted text-muted-foreground"}`}>
              <span className="font-mono">{i + 1}.</span>
              <span>{label}</span>
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="border-border" data-testid="tile-template">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Download className="h-5 w-5" />
              </div>
              <div className="font-heading text-lg font-bold">Option A · Vorlage nutzen</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Lade unsere Vorlage herunter, kopiere deine Excel-Daten in die vordefinierten Spalten und lade sie hier wieder hoch. Garantiert saubere Zuordnung.
              </p>
              <Button variant="outline" className="mt-4 w-full" onClick={downloadTemplate} data-testid="btn-download-template">
                <Download className="h-4 w-4 mr-2" /> Vorlage herunterladen (.xlsx)
              </Button>
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="tile-auto">
            <CardContent className="p-6">
              <div className="h-10 w-10 rounded-md bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-4">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="font-heading text-lg font-bold">Option B · Eigene Excel laden</div>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Deine bestehende Excel direkt hochladen. Das System erkennt automatisch die Spalten (Name, PLZ, Netzbetreiber …). Du kannst danach jede Zuordnung noch anpassen.
              </p>
              <label className="mt-4 block">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleUpload} data-testid="import-file-input" disabled={busy} />
                <div className="inline-flex w-full items-center justify-center gap-2 h-10 px-4 rounded-md bg-primary text-primary-foreground cursor-pointer hover:bg-primary/90 text-sm font-medium">
                  <Upload className="h-4 w-4" /> {busy ? "Lade …" : "Excel hochladen (.xlsx)"}
                </div>
              </label>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 2 && currentSheet && (
        <div className="space-y-4">
          {sheets.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="sheet-tabs">
              <span className="text-xs text-muted-foreground">Sheet:</span>
              {sheets.map((s, i) => (
                <button key={s.name} onClick={() => changeSheet(i)}
                  data-testid={`sheet-tab-${i}`}
                  className={`text-xs px-3 py-1.5 rounded-md border ${activeSheet === i ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/40"}`}>
                  {s.name} <span className="opacity-60">({s.rows.length})</span>
                </button>
              ))}
            </div>
          )}

          <Card className="border-border">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading text-lg font-bold">Spalten zuordnen</div>
                  <div className="text-xs text-muted-foreground">
                    {mappedCount} von {currentSheet.headers.length} Spalten zugewiesen · {currentSheet.rows.length} Datenzeilen
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="mapping-grid">
                {currentSheet.headers.map((h) => (
                  <div key={h} className="flex items-center gap-2 border border-border rounded-md p-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Excel-Spalte</div>
                      <div className="text-sm font-medium truncate" title={h}>{h}</div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <Select value={mapping[h] || IGNORE} onValueChange={(v) => setMapping((m) => ({ ...m, [h]: v }))}>
                      <SelectTrigger className="w-[220px] text-xs" data-testid={`map-${h}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={IGNORE}>— ignorieren —</SelectItem>
                        {fieldOptions.map((f) => (
                          <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>

              {/* Projekt-Option */}
              <div className="border-t border-border pt-4">
                <div className="flex items-center gap-3">
                  <Switch checked={createProject} onCheckedChange={setCreateProject} id="create-project" data-testid="switch-create-project" />
                  <div className="flex-1">
                    <Label htmlFor="create-project" className="cursor-pointer">Zusätzlich Projekte anlegen</Label>
                    <div className="text-[11px] text-muted-foreground">Pro Zeile wird auch ein Installations-Projekt (PV oder Wärmepumpe) erstellt und mit der Anmeldung verknüpft.</div>
                  </div>
                  {createProject && (
                    <Select value={defaultProjectType} onValueChange={setDefaultProjectType}>
                      <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pv">Photovoltaik (Standard)</SelectItem>
                        <SelectItem value="waermepumpe">Wärmepumpe (Standard)</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </div>

              {/* Vorschau */}
              <div>
                <div className="text-xs text-muted-foreground mb-2">Vorschau (erste 5 Zeilen)</div>
                <div className="border border-border rounded-md overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {currentSheet.headers.slice(0, 8).map((h) => (
                          <TableHead key={h} className="text-[11px]">
                            <div className="truncate max-w-[140px]">{h}</div>
                            {mapping[h] && mapping[h] !== IGNORE && (
                              <div className="text-[9px] text-primary font-normal normal-case">→ {fieldOptions.find((f) => f.key === mapping[h])?.label}</div>
                            )}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentSheet.rows.slice(0, 5).map((r, i) => (
                        <TableRow key={i}>
                          {currentSheet.headers.slice(0, 8).map((h) => (
                            <TableCell key={h} className="text-xs truncate max-w-[140px]">{r[h] || <span className="text-muted-foreground italic">—</span>}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 flex-wrap gap-2">
                <Button variant="outline" onClick={() => setStep(1)}>Zurück</Button>
                <Button onClick={execute} disabled={busy || mappedCount === 0} data-testid="btn-execute">
                  {busy ? "Importiere …" : `${currentSheet.rows.length} Zeilen importieren`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 3 && result && (
        <Card className="border-border" data-testid="result-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              <div>
                <div className="font-heading text-2xl font-bold">Import abgeschlossen</div>
                <div className="text-sm text-muted-foreground">Alles wurde in dein System übernommen.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                { label: "Auftraggeber neu", value: result.created_auftraggeber, cls: "bg-primary/10 text-primary" },
                { label: "Auftraggeber verknüpft", value: result.linked_auftraggeber, cls: "bg-sky-500/10 text-sky-500" },
                { label: "Anmeldungen", value: result.created_anmeldungen, cls: "bg-emerald-500/10 text-emerald-500" },
                { label: "Projekte", value: result.created_projekte, cls: "bg-orange-500/10 text-orange-500" },
              ].map((k) => (
                <div key={k.label} className="border border-border rounded-md p-4">
                  <div className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${k.cls}`}>{k.label}</div>
                  <div className="font-heading text-3xl font-bold mt-2">{k.value}</div>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4 border border-amber-300 bg-amber-50 dark:bg-amber-900/10 rounded-md p-4">
                <div className="flex items-center gap-2 text-amber-900 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">{result.errors.length} Zeile(n) übersprungen</span>
                </div>
                <ul className="mt-2 space-y-0.5 text-xs text-amber-900 dark:text-amber-300 max-h-40 overflow-auto">
                  {result.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Zeile {e.row}: {e.error}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex items-center gap-2 mt-6">
              <Button variant="outline" onClick={() => { setStep(1); setResult(null); setSheets([]); setMapping({}); }} data-testid="btn-import-more">
                Weitere Datei importieren
              </Button>
              <Link to="/anmeldungen"><Button>Zu den Anmeldungen</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-muted/30" data-testid="hints-card">
        <CardContent className="p-4 text-xs text-muted-foreground space-y-1.5">
          <div className="font-medium text-foreground flex items-center gap-2"><FileSpreadsheet className="h-3.5 w-3.5" /> Tipps</div>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Auftraggeber werden anhand des Namens gruppiert — doppelte Firmen werden nicht neu angelegt.</li>
            <li>Anlagentypen kannst du kommagetrennt eintragen: <code>pv, speicher, wallbox</code>.</li>
            <li>Status-Werte: <code>neu, angemeldet, fertiggemeldet, termin_vereinbart, zaehler_gesetzt, abgeschlossen</code>.</li>
            <li>Anrede: <code>Herr / Frau / Firma / divers</code>.</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
